import { z } from 'zod';

export const AgentCompletionToolSchemas: Record<string, z.ZodTypeAny> = {
  convert_currency: z.object({
    amount: z.number().describe('Amount of currency to convert'),
    from_currency_code: z.string().describe('ISO 4217 currency code `amount` corresponds to'),
    to_currency_code: z.string().describe('ISO 4217 currency code return value corresponds to'),
  }),
  search_lat_lon: z.object({
    latitude: z.number().describe('Latitude to search'),
    longitude: z.number().describe('Longitude to search'),
  }),
  search_location_around_lat_lon: z.object({
    location: z.string().describe('Surface form query defining the location'),
    latitude: z
      .number()
      .optional()
      .describe('Latitude to search around. Defaults to current latitude if not provided'),
    longitude: z
      .number()
      .optional()
      .describe('Longitude to search around. Defaults to current longitude if not provided'),
  }),
  search_stock: z.object({
    query: z
      .string()
      .describe('a company name (Apple), stock symbol (AAPL) exchange name (NASDAQ)'),
  }),
  search_weather_around_lat_lon: z.object({
    days: z
      .number()
      .int()
      .optional()
      .describe(
        'Number of days to search for past the current time. Defaults to current day if not provided.',
      ),
    latitude: z
      .number()
      .optional()
      .describe('Latitude to search around. Defaults to current latitude if not provided'),
    longitude: z
      .number()
      .optional()
      .describe('Longitude to search around. Defaults to current longitude if not provided'),
  }),
  calculate_lat_lon_distance: z.object({
    latitude_0: z.number().describe('First Latitude.'),
    longitude_0: z.number().describe('First Longitude.'),
    latitude_1: z.number().describe('Second Latitude.'),
    longitude_1: z.number().describe('Second Longitude.'),
  }),
};

export const FlamingoAgenticImagineToolSchemas: Record<string, z.ZodTypeAny> = {
  imagine_create: z.object({
    prompt: z.string().describe('Prompt to imagine'),
    creation_type: z.enum(['image']).describe('Type of creation to imagine'),
    include_user: z.string().describe('Whether to include the user in the creation'),
  }),
  imagine_modify: z.object({
    prompt: z.string().describe('Prompt to modify'),
    modification_type: z
      .enum([
        'add',
        'remove',
        'replace',
        'inpaint',
        'backdrop',
        'visualtext',
        'age_change',
        'expression_change',
        'style',
        'scene',
        'other',
      ])
      .describe('Type of modification to apply'),
    file_paths: z.array(z.string()).describe('Paths to files to modify'),
  }),
  imagine_animate: z.object({
    prompt: z.string().describe('Prompt to animate'),
    creation_type: z.enum(['animation']).describe('Type of creation to animate'),
    file_paths: z.array(z.string()).describe('Paths to files to animate'),
  }),
};

export const KratosAfmAgentSanPri225ToolSchemas: Record<string, z.ZodTypeAny> = {
  search_place: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2. Also affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    latitude: z
      .number()
      .describe('Center latitude of the search radius. Must be provided with longitude and radius.')
      .optional(),
    longitude: z
      .number()
      .describe('Center longitude of the search radius. Must be provided with latitude and radius.')
      .optional(),
    query: z
      .string()
      .describe(
        "The text string on which to search, for example: 'restaurant' or '123 Main Street'. This must be a place name, address, or category of establishments.",
      ),
    radius: z
      .number()
      .describe(
        'Defines the distance (in meters) within which to return place results. Ignored unless latitude and longitude are specified. Max value of 50,000 meters.',
      )
      .optional(),
  }),
  place_details: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2. Also affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    place_id: z
      .string()
      .describe('Unique id representing the location. Returned by search_place tool.'),
  }),
  get_directions: z.object({
    arrival_time: z
      .string()
      .describe(
        'Expected arrival time in ISO 8601 format with timezone offset. E.g. 2024-12-03T22:26-08:00. Can only be set if departure_time is not.',
      )
      .optional(),
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2. Also affects language codes including en, zh, pt, es.',
      )
      .optional(),
    departure_time: z
      .string()
      .describe(
        'Expected departure time in ISO 8601 format with timezone offset. E.g. 2024-12-03T22:26-08:00. Can only be set if arrival_time is not.',
      )
      .optional(),
    destination: z
      .string()
      .describe('place_id representing destination. Returned by search_place tool.'),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    mode: z
      .string()
      .describe('Transportation mode: driving, walking, bicycling, transit. Defaults to driving.')
      .optional(),
    origin: z.string().describe('place_id representing origin. Returned by search_place tool.'),
    traffic_model: z
      .string()
      .describe(
        'Traffic assumptions: best_guess, pessimistic, optimistic. Only for driving directions with departure_time.',
      )
      .optional(),
  }),
  convert_place_id_lat_lon_address: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2. Also affects language codes including en, zh, pt, es.',
      )
      .optional(),
    formatted_address: z.string().describe('Formatted address to search for.').optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    latitude: z
      .number()
      .describe('Latitude to search for. Must be provided with longitude.')
      .optional(),
    longitude: z
      .number()
      .describe('Longitude to search for. Must be provided with latitude.')
      .optional(),
    place_id: z
      .string()
      .describe('Unique id representing the location. Returned by search_place tool.')
      .optional(),
  }),
  get_current_location: z.object({}),
};

export const KratosAfmAgentPeg003ToolSchemas: Record<string, z.ZodTypeAny> = {
  search_location_around_lat_lon: z.object({
    latitude: z
      .number()
      .describe('Latitude to search around. Defaults to current latitude if not provided.')
      .optional(),
    location: z.string().describe('Surface form query defining the location.'),
    longitude: z
      .number()
      .describe('Longitude to search around. Defaults to current longitude if not provided.')
      .optional(),
  }),

  search_athlete: z.object({
    query: z
      .string()
      .describe(
        "The text string on which to search, for example: 'gsw players' or 'point guard of gsw'. This must be an athlete related query.",
      ),
  }),

  search_game_event: z.object({
    query: z
      .string()
      .describe(
        "The text string on which to search, for example: 'next gsw game' or 'gsw vs lakers a week ago'. This must be a sports game related query.",
      ),
  }),

  search_stock: z.object({
    query: z
      .string()
      .describe('A company name (Apple), stock symbol (AAPL), or exchange name (NASDAQ).'),
  }),

  search_weather_around_lat_lon: z.object({
    days: z
      .number()
      .int()
      .describe(
        'Number of days to search for past the current time. Defaults to current day if not provided.',
      )
      .optional(),
    latitude: z
      .number()
      .describe('Latitude to search around. Defaults to current latitude if not provided.')
      .optional(),
    longitude: z
      .number()
      .describe('Longitude to search around. Defaults to current longitude if not provided.')
      .optional(),
  }),
};
export const KratosAfmAgentSanPri26111ToolSchemas: Record<string, z.ZodTypeAny> = {
  search_place: z.object({
    country: z.string().optional(),
    language: z.string().optional(),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    query: z.string(),
    radius: z.number().optional(),
  }),

  place_details: z.object({
    country: z.string().optional(),
    language: z.string().optional(),
    place_id: z.string(),
  }),

  get_directions: z.object({
    arrival_time: z.string().optional(),
    country: z.string().optional(),
    departure_time: z.string().optional(),
    destination: z.string(),
    language: z.string().optional(),
    mode: z.string().optional(),
    origin: z.string(),
    traffic_model: z.string().optional(),
  }),

  convert_place_id_lat_lon_address: z.object({
    country: z.string().optional(),
    formatted_address: z.string().optional(),
    language: z.string().optional(),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    place_id: z.string().optional(),
  }),

  create_calendar: z.object({
    title: z.string(),
  }),

  search_calendars: z.object({
    calendar_id: z.string().optional(),
    title: z.string().optional(),
  }),

  modify_calendar: z.object({
    calendar_id: z.string(),
    title: z.string(),
  }),

  remove_calendar: z.object({
    calendar_id: z.string(),
  }),

  create_calendar_event: z.object({
    attendee_emails: z.array(z.any()).optional(),
    attendee_names: z.array(z.any()).optional(),
    calendar_id: z.string(),
    description: z.string().optional(),
    end_datetime: z.string(),
    formatted_address: z.string().optional(),
    is_all_day: z.boolean().optional(),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    place_id: z.string().optional(),
    recurrence_frequency: z.string().optional(),
    recurrence_interval: z.number().int().optional(),
    recurrence_until_datetime: z.string().optional(),
    start_datetime: z.string(),
    title: z.string(),
  }),

  search_calendar_events: z.object({
    attendee_emails: z.array(z.any()).optional(),
    attendee_names: z.array(z.any()).optional(),
    calendar_event_id: z.string().optional(),
    calendar_id: z.string().optional(),
    datetime_range_lowerbound: z.string().optional(),
    datetime_range_upperbound: z.string().optional(),
    description: z.string().optional(),
    formatted_address: z.string().optional(),
    is_all_day: z.boolean().optional(),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    place_id: z.string().optional(),
    recurrence_frequency: z.string().optional(),
    recurrence_interval: z.number().int().optional(),
    recurrence_until_datetime: z.number().int().optional(),
    title: z.string().optional(),
  }),

  remove_calendar_event: z.object({
    calendar_event_id: z.string(),
  }),

  modify_calendar_event: z.object({
    attendee_emails: z.array(z.any()).optional(),
    attendee_names: z.array(z.any()).optional(),
    calendar_event_id: z.string(),
    calendar_id: z.string().optional(),
    description: z.string().optional(),
    end_datetime: z.string().optional(),
    formatted_address: z.string().optional(),
    is_all_day: z.boolean().optional(),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    place_id: z.string().optional(),
    recurrence_frequency: z.string().optional(),
    recurrence_interval: z.number().int().optional(),
    recurrence_until_datetime: z.number().int().optional(),
    start_datetime: z.string().optional(),
    title: z.string().optional(),
  }),

  get_system_settings: z.object({}),

  set_system_settings: z.object({
    cellular: z.boolean().optional(),
    locale: z.string().optional(),
    location_service: z.boolean().optional(),
    low_battery_mode: z.boolean().optional(),
    utc_offset_seconds: z.number().int().optional(),
    wifi: z.boolean().optional(),
  }),

  get_current_location: z.object({}),

  get_current_iso_8601_datetime_with_utc_offset: z.object({}),

  iso_8601_datetime_with_utc_offset_to_iso_weekday: z.object({
    datetime_str: z.string(),
  }),
};

export const KratosAfmAgentSanPro282ToolSchemas: Record<string, z.ZodTypeAny> = {
  search_place: z.object({
    country: z
      .string()
      .describe(
        "The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.\nIf language and country are None, country will be inferred from user's locale if it has been set.",
      )
      .optional(),
    language: z
      .string()
      .describe(
        "The language in which to return results. Specified in lowercase ISO 639 Set 1.\nIf language and country are None, language will be inferred from user's locale if it has been set.",
      )
      .optional(),
    latitude: z
      .number()
      .describe('Center latitude of the search radius. Must be provided with longitude and radius.')
      .optional(),
    longitude: z
      .number()
      .describe('Center longitude of the search radius. Must be provided with latitude and radius.')
      .optional(),
    query: z
      .string()
      .describe(
        'The text string on which to search, for example: "restaurant" or "123 Main Street".\nThis must a place name, address, or category of establishments.\nAny other types of input can generate errors and are not guaranteed to return valid results.\nThe service will return candidate matches based on this string and order\nthe results based on their perceived relevance.',
      ),
    radius: z
      .number()
      .describe(
        'Defines the distance (in meters) within which to return place results. Ignored\nunless latitude and longitude are specified.\nYou may bias results to a specified circle by passing latitude longitude and a radius\nparameter. Doing so instructs the service to prefer showing results within that circle;\nresults outside the defined area may still be displayed. The radius will automatically be\nclamped to a maximum value of 50,000 meters.',
      )
      .optional(),
  }),

  place_details: z.object({
    country: z
      .string()
      .describe(
        "The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.\nIf language and country are None, country will be inferred from user's locale if it has been set.",
      )
      .optional(),
    language: z
      .string()
      .describe(
        "The language in which to return results. Specified in lowercase ISO 639 Set 1.\nIf language and country are None, language will be inferred from user's locale if it has been set.",
      )
      .optional(),
    place_id: z
      .string()
      .describe('Unique id representing the location. Returned by search_place tool.'),
  }),

  get_directions: z.object({
    arrival_time: z
      .string()
      .describe(
        'Expected arrival time in ISO 8601 format with timezone offset.\nE.g. 2024-12-03T22:26-08:00. Can only be set if departure_time is not.',
      )
      .optional(),
    country: z
      .string()
      .describe(
        "The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.\nIf language and country are None, country will be inferred from user's locale if it has been set.",
      )
      .optional(),
    departure_time: z
      .string()
      .describe(
        'Expected departure time in ISO 8601 format with timezone offset.\nE.g. 2024-12-03T22:26-08:00. Can only be set if arrival_time is not.',
      )
      .optional(),
    destination: z
      .string()
      .describe('place_id representing destination. Returned by search_place tool.'),
    language: z
      .string()
      .describe(
        "The language in which to return results. Specified in lowercase ISO 639 Set 1.\nIf language and country are None, language will be inferred from user's locale if it has been set.",
      )
      .optional(),
    mode: z
      .string()
      .describe(
        'For the calculation of distances and directions, you may specify\nthe transportation mode to use. By default, driving mode is used.\nBy default, directions are calculated as driving directions.\nThe following travel modes are supported:\ndriving, walking, bicycling, transit',
      )
      .optional(),
    origin: z.string().describe('place_id representing origin. Returned by search_place tool.'),
    traffic_model: z
      .string()
      .describe(
        'Specifies the assumptions to use when calculating time in traffic.\nThis setting affects the value returned in the duration_in_traffic field in the response,\nwhich contains the predicted time in traffic based on historical averages.\nThe traffic_model parameter may only be specified for driving directions where\nthe request includes a departure_time. The available values for this parameter are:\nbest_guess, pessimistic, optimistic',
      )
      .optional(),
  }),

  convert_place_id_lat_lon_address: z.object({
    country: z
      .string()
      .describe(
        "The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.\nIf language and country are None, country will be inferred from user's locale if it has been set.",
      )
      .optional(),
    formatted_address: z.string().describe('Formatted address to search for.').optional(),
    language: z
      .string()
      .describe(
        "The language in which to return results. Specified in lowercase ISO 639 Set 1.\nIf language and country are None, language will be inferred from user's locale if it has been set.",
      )
      .optional(),
    latitude: z
      .number()
      .describe('Latitude to search for. Must be provided with longitude.')
      .optional(),
    longitude: z
      .number()
      .describe('Longitude to search for. Must be provided with latitude.')
      .optional(),
    place_id: z
      .string()
      .describe('Unique id representing the location. Returned by search_place tool.')
      .optional(),
  }),

  search_place_and_specify_return_fields: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    fields: z
      .string()
      .describe(
        'The place data type to return. For example, `fields=name` specifies the return data type has\nonly the `name` key. Acceptable parameters are `formatted_address`, `geometry`, `name`,\n`place_id` and `all`. `all` means return all canonical types.\nMultiple parameters can be specified in a comma-separated list, e.g., `fields=geometry,name,place_id`',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    latitude: z
      .number()
      .describe('Center latitude of the search radius. Must be provided with longitude and radius.')
      .optional(),
    longitude: z
      .number()
      .describe('Center longitude of the search radius. Must be provided with latitude and radius.')
      .optional(),
    query: z
      .string()
      .describe(
        'The text string on which to search, for example: "restaurant" or "123 Main Street".\nThis must a place name, address, or category of establishments.\nAny other types of input can generate errors and are not guaranteed to return valid results.\nThe service will return candidate matches based on this string and order\nthe results based on their perceived relevance.',
      ),
    radius: z
      .number()
      .describe(
        'Defines the distance (in meters) within which to return place results. Ignored\nunless latitude and longitude are specified.\nYou may bias results to a specified circle by passing latitude longitude and a radius\nparameter. Doing so instructs the service to prefer showing results within that circle;\nresults outside the defined area may still be displayed. The radius will automatically be\nclamped to a maximum value of 50,000 meters.',
      )
      .optional(),
  }),

  place_details_and_specify_return_fields: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    fields: z
      .string()
      .describe(
        'The place data type to return. For example, `fields=name` specifies the return data type has\nonly the `name` key. Acceptable parameters are `formatted_address`, `geometry`, `formatted_phone_number`,\n`name`, `price_level`, `rating`, `types`, `reviews`, `delivery`, `reservable`, `opening_hours`, and `all`.\n`all` means return all data types mentioned above.\nMultiple parameters can be specified in a comma-separated list, e.g., `fields=geometry,name,geometry`',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    place_id: z
      .string()
      .describe('Unique id representing the location. Returned by search_place tool.'),
  }),

  search_place_and_return_address: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    latitude: z
      .number()
      .describe('Center latitude of the search radius. Must be provided with longitude and radius.')
      .optional(),
    longitude: z
      .number()
      .describe('Center longitude of the search radius. Must be provided with latitude and radius.')
      .optional(),
    query: z
      .string()
      .describe(
        'The text string on which to search, for example: "restaurant" or "123 Main Street".\nThis must a place name, address, or category of establishments.\nAny other types of input can generate errors and are not guaranteed to return valid results.\nThe service will return candidate matches based on this string and order\nthe results based on their perceived relevance.',
      ),
    radius: z
      .number()
      .describe(
        'Defines the distance (in meters) within which to return place results. Ignored\nunless latitude and longitude are specified.\nYou may bias results to a specified circle by passing latitude longitude and a radius\nparameter. Doing so instructs the service to prefer showing results within that circle;\nresults outside the defined area may still be displayed. The radius will automatically be\nclamped to a maximum value of 50,000 meters.',
      )
      .optional(),
  }),

  search_place_and_return_name: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    latitude: z
      .number()
      .describe('Center latitude of the search radius. Must be provided with longitude and radius.')
      .optional(),
    longitude: z
      .number()
      .describe('Center longitude of the search radius. Must be provided with latitude and radius.')
      .optional(),
    query: z
      .string()
      .describe(
        'The text string on which to search, for example: "restaurant" or "123 Main Street".\nThis must a place name, address, or category of establishments.\nAny other types of input can generate errors and are not guaranteed to return valid results.\nThe service will return candidate matches based on this string and order\nthe results based on their perceived relevance.',
      ),
    radius: z
      .number()
      .describe(
        'Defines the distance (in meters) within which to return place results. Ignored\nunless latitude and longitude are specified.\nYou may bias results to a specified circle by passing latitude longitude and a radius\nparameter. Doing so instructs the service to prefer showing results within that circle;\nresults outside the defined area may still be displayed. The radius will automatically be\nclamped to a maximum value of 50,000 meters.',
      )
      .optional(),
  }),

  search_place_and_return_place_id: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    latitude: z
      .number()
      .describe('Center latitude of the search radius. Must be provided with longitude and radius.')
      .optional(),
    longitude: z
      .number()
      .describe('Center longitude of the search radius. Must be provided with latitude and radius.')
      .optional(),
    query: z
      .string()
      .describe(
        'The text string on which to search, for example: "restaurant" or "123 Main Street".\nThis must a place name, address, or category of establishments.\nAny other types of input can generate errors and are not guaranteed to return valid results.\nThe service will return candidate matches based on this string and order\nthe results based on their perceived relevance.',
      ),
    radius: z
      .number()
      .describe(
        'Defines the distance (in meters) within which to return place results. Ignored\nunless latitude and longitude are specified.\nYou may bias results to a specified circle by passing latitude longitude and a radius\nparameter. Doing so instructs the service to prefer showing results within that circle;\nresults outside the defined area may still be displayed. The radius will automatically be\nclamped to a maximum value of 50,000 meters.',
      )
      .optional(),
  }),

  search_place_and_return_geometry: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    latitude: z
      .number()
      .describe('Center latitude of the search radius. Must be provided with longitude and radius.')
      .optional(),
    longitude: z
      .number()
      .describe('Center longitude of the search radius. Must be provided with latitude and radius.')
      .optional(),
    query: z
      .string()
      .describe(
        'The text string on which to search, for example: "restaurant" or "123 Main Street".\nThis must a place name, address, or category of establishments.\nAny other types of input can generate errors and are not guaranteed to return valid results.\nThe service will return candidate matches based on this string and order\nthe results based on their perceived relevance.',
      ),
    radius: z
      .number()
      .describe(
        'Defines the distance (in meters) within which to return place results. Ignored\nunless latitude and longitude are specified.\nYou may bias results to a specified circle by passing latitude longitude and a radius\nparameter. Doing so instructs the service to prefer showing results within that circle;\nresults outside the defined area may still be displayed. The radius will automatically be\nclamped to a maximum value of 50,000 meters.',
      )
      .optional(),
  }),

  place_details_and_return_geometry: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    place_id: z
      .string()
      .describe('Unique id representing the location. Returned by search_place tool.'),
  }),

  place_details_and_return_address: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    place_id: z
      .string()
      .describe('Unique id representing the location. Returned by search_place tool.'),
  }),

  place_details_and_return_phone_number: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    place_id: z
      .string()
      .describe('Unique id representing the location. Returned by search_place tool.'),
  }),

  place_details_and_return_name: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    place_id: z
      .string()
      .describe('Unique id representing the location. Returned by search_place tool.'),
  }),

  place_details_and_return_price_level: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    place_id: z
      .string()
      .describe('Unique id representing the location. Returned by search_place tool.'),
  }),

  place_details_and_return_rating: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    place_id: z
      .string()
      .describe('Unique id representing the location. Returned by search_place tool.'),
  }),

  place_details_and_return_types: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    place_id: z
      .string()
      .describe('Unique id representing the location. Returned by search_place tool.'),
  }),

  place_details_and_return_reviews: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    place_id: z
      .string()
      .describe('Unique id representing the location. Returned by search_place tool.'),
  }),

  place_details_and_return_delivery: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    place_id: z
      .string()
      .describe('Unique id representing the location. Returned by search_place tool.'),
  }),

  place_details_and_return_reservable: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    place_id: z
      .string()
      .describe('Unique id representing the location. Returned by search_place tool.'),
  }),

  place_details_and_return_opening_hours: z.object({
    country: z
      .string()
      .describe(
        'The country to search in. Specified in lowercase ISO 3166-1 alpha-2.\nAlso affects language codes including en, zh, pt, es.',
      )
      .optional(),
    language: z
      .string()
      .describe('The language in which to return results. Specified in lowercase ISO 639 Set 1.')
      .optional(),
    place_id: z
      .string()
      .describe('Unique id representing the location. Returned by search_place tool.'),
  }),

  create_calendar: z.object({
    title: z.string().describe('Title summarizing the new calendar. E.g. Work.'),
  }),

  search_calendars: z.object({
    calendar_id: z
      .string()
      .describe('String format unique identifier for the calendar. Will be exact matched.')
      .optional(),
    title: z
      .string()
      .describe('Search query for title field. Will be matched based on semantic similarity')
      .optional(),
  }),

  modify_calendar: z.object({
    calendar_id: z.string().describe('UUID of calendar to be edited.'),
    title: z.string().describe('New title for the calendar. E.g. Home.'),
  }),

  remove_calendar: z.object({
    calendar_id: z.string().describe('UUID of calendar to be removed.'),
  }),

  create_calendar_event: z.object({
    attendee_emails: z
      .array(z.string())
      .describe(
        'A list of attendee emails. Forms a 1 to 1 mapping with attendee_names.\nUse null element for attendee without an email.',
      )
      .optional(),
    attendee_names: z
      .array(z.string())
      .describe('A list of attendee names. Forms a 1 to 1 mapping with attendee_email.')
      .optional(),
    calendar_id: z.string().describe('UUID of calendar this event belongs to.'),
    description: z.string().describe('Detailed description of the event.').optional(),
    end_datetime: z
      .string()
      .describe(
        'ISO 8601 datetime string with UTC timezone offset representing event end time,\nexclusive. E.g. 2024-12-09T16:00-08:00.',
      ),
    formatted_address: z.string().describe('formatted_address of the event location.').optional(),
    is_all_day: z
      .boolean()
      .describe(
        'Boolean value indicating if this is an all day event. If true, hours and minute\ninfo in start_datetime and end_datetime will be discarded.',
      )
      .optional(),
    latitude: z.number().describe('Latitude of the event location.').optional(),
    longitude: z.number().describe('Longitude of the event location.').optional(),
    place_id: z.string().describe('place_id of the event location.').optional(),
    recurrence_frequency: z
      .enum(['YEARLY', 'MONTHLY', 'WEEKLY', 'DAILY'])
      .describe('Unit of recurrence frequency.')
      .optional(),
    recurrence_interval: z
      .number()
      .int()
      .describe(
        'Combined with unit, forms a recurrence rule.\nE.g. interval of 2 and frequency of WEEKLY means biweekly.',
      )
      .optional(),
    recurrence_until_datetime: z
      .string()
      .describe('ISO 8601 representing end of recurrence. E.g. 2024-12-31T15:00-08:00.')
      .optional(),
    start_datetime: z
      .string()
      .describe(
        'ISO 8601 datetime string with UTC timezone offset representing event start time,\ninclusive. E.g. 2024-12-09T15:00-08:00.',
      ),
    title: z.string().describe('Title or summary for the event.'),
  }),

  search_calendar_events: z.object({
    attendee_emails: z
      .array(z.string())
      .describe(
        'List of attendee emails. Will be exact matched. All provided emails must each find\na match.',
      )
      .optional(),
    attendee_names: z
      .array(z.string())
      .describe(
        'List of attendee names. Will be semantically matched. All provided names must each\nfind at least a match.',
      )
      .optional(),
    calendar_event_id: z
      .string()
      .describe('String format unique identifier for calendar event. Will be exact matched')
      .optional(),
    calendar_id: z
      .string()
      .describe(
        'String format unique identifier for calendar to search under. Will be exact matched.',
      )
      .optional(),
    datetime_range_lowerbound: z
      .string()
      .describe(
        'Lowerbound of datetime range to search for calendar event.\nAny overlap between event [start_datetime, end_datetime) and\n[Lowerbound, Upperbound) is considered a match.',
      )
      .optional(),
    datetime_range_upperbound: z
      .string()
      .describe(
        'Upperbound of datetime range to search for calendar event.\nAny overlap between event [start_datetime, end_datetime) and\n[Lowerbound, Upperbound) is considered a match.',
      )
      .optional(),
    description: z
      .string()
      .describe('Detailed description of calendar event. Will be semantically matched')
      .optional(),
    formatted_address: z
      .string()
      .describe('Formatted address of location. Will be semantically matched.')
      .optional(),
    is_all_day: z
      .boolean()
      .describe('Boolean indicating all day events. Will be exact matched.')
      .optional(),
    latitude: z
      .number()
      .describe('Latitude of location. Will be matched with 0.01 degree tolerance.')
      .optional(),
    longitude: z
      .number()
      .describe('Longitude of location. Will be matched with 0.01 degree tolerance.')
      .optional(),
    place_id: z.string().describe('place_id of location. Will be exact matched.').optional(),
    recurrence_frequency: z
      .enum(['YEARLY', 'MONTHLY', 'WEEKLY', 'DAILY'])
      .describe('Enum indicating recurrence frequency. Will be exact matched.')
      .optional(),
    recurrence_interval: z
      .number()
      .int()
      .describe('Enum indicating recurrence interval. Will be exact matched.')
      .optional(),
    recurrence_until_datetime: z
      .number()
      .int()
      .describe('ISO 8601 representing end of recurrence. Will be exact matched.')
      .optional(),
    title: z
      .string()
      .describe('Title summarizing calendar event. Will be semantically matched')
      .optional(),
  }),

  remove_calendar_event: z.object({
    calendar_event_id: z
      .string()
      .describe(
        'UUID of calendar event to be removed. Note that in the case of a single instance of\nrecurring event, using its calendar_event_id only removes this event itself. To delete\nall instances of a recurring event, use the calendar_event_id of the parent of\na recurring instance (e.g. the recurrence_parent_id of the instance).',
      ),
  }),

  modify_calendar_event: z.object({
    attendee_emails: z.array(z.string()).describe('New list of attendee emails.').optional(),
    attendee_names: z.array(z.string()).describe('New list of attendee names.').optional(),
    calendar_event_id: z
      .string()
      .describe(
        'UUID of calendar event to be modified. Note that in the case of a single instance of\nrecurring event, using its calendar_event_id only modifies this event itself.\nTo modify all instances of a recurring event, use the calendar_event_id of\nthe parent of a recurring instance (e.g. the recurrence_parent_id of the instance).\nModifying a parent recurrence event means previously occurred\nchild instances are removed, and replaced with new instances with new uuids.',
      ),
    calendar_id: z
      .string()
      .describe('New string format unique identifier for calendar this event belongs to.')
      .optional(),
    description: z.string().describe('New detailed description of calendar event.').optional(),
    end_datetime: z
      .string()
      .describe(
        'New ISO 8601 datetime string with UTC timezone offset representing event end time,\nexclusive. E.g. 2024-12-09T16:00-08:00.',
      )
      .optional(),
    formatted_address: z.string().describe('New formatted address of location.').optional(),
    is_all_day: z.boolean().describe('Boolean indicating all day events.').optional(),
    latitude: z.number().describe('New latitude of location.').optional(),
    longitude: z.number().describe('New longitude of location.').optional(),
    place_id: z.string().describe('New place_id of location.').optional(),
    recurrence_frequency: z
      .enum(['YEARLY', 'MONTHLY', 'WEEKLY', 'DAILY'])
      .describe('Enum indicating new recurrence frequency.')
      .optional(),
    recurrence_interval: z
      .number()
      .int()
      .describe('Enum indicating new recurrence interval.')
      .optional(),
    recurrence_until_datetime: z
      .number()
      .int()
      .describe('ISO 8601 representing end of recurrence. Will be exact matched.')
      .optional(),
    start_datetime: z
      .string()
      .describe(
        'New ISO 8601 datetime string with UTC timezone offset representing event start time,\ninclusive. E.g. 2024-12-09T15:00-08:00.',
      )
      .optional(),
    title: z.string().describe('New title summarizing calendar event.').optional(),
  }),

  get_system_settings: z.object({}),

  set_system_settings: z.object({
    cellular: z.boolean().describe('True to turn on cellular service. False otherwise.').optional(),
    locale: z
      .string()
      .describe('Locale to set to. In the form of <language>_<COUNTRY>, e.g. en_US.')
      .optional(),
    location_service: z
      .boolean()
      .describe('True to turn on location service. False otherwise.')
      .optional(),
    low_battery_mode: z
      .boolean()
      .describe('True to turn on low battery mode. False otherwise.')
      .optional(),
    utc_offset_seconds: z
      .number()
      .int()
      .describe(
        'Timezone to set to. In the form of number of offset seconds against UTC.\nE.g. -28800 represents UTC−08:00.',
      )
      .optional(),
    wifi: z.boolean().describe('True to turn on wifi service. False otherwise.').optional(),
  }),

  get_current_location: z.object({}),

  get_cellular_status: z.object({}),

  get_wifi_status: z.object({}),

  get_location_service_status: z.object({}),

  get_low_battery_mode_status: z.object({}),

  get_locale: z.object({}),

  get_timezone_utc_offset_seconds: z.object({}),

  get_current_latitude: z.object({}),

  get_current_longitude: z.object({}),

  get_current_place_id: z.object({}),

  get_current_formatted_address: z.object({}),

  set_cellular_status: z.object({
    status: z.boolean().describe('True to turn on cellular service. False otherwise.'),
  }),

  set_wifi_status: z.object({
    status: z.boolean().describe('True to turn on wifi service. False otherwise.'),
  }),

  set_location_service_status: z.object({
    status: z.boolean().describe('True to turn on location service. False otherwise.'),
  }),

  set_low_battery_mode_status: z.object({
    status: z.boolean().describe('True to turn on low battery mode. False otherwise.'),
  }),

  set_locale: z.object({
    locale: z
      .string()
      .describe('Locale to set to. In the form of <language>_<COUNTRY>, e.g. en_US.'),
  }),

  set_timezone_utc_offset_seconds: z.object({
    utc_offset_seconds: z
      .number()
      .int()
      .describe(
        'Timezone to set to. In the form of number of offset seconds against UTC.\nE.g. -28800 represents UTC−08:00.',
      ),
  }),

  get_system_settings_field: z.object({
    field: z
      .enum([
        'cellular',
        'wifi',
        'location_service',
        'low_battery_mode',
        'locale',
        'utc_offset_seconds',
      ])
      .describe(
        'Name of the system setting to retrieve. Can choose from\ncellular, wifi, location_service, low_battery_mode, locale, utc_offset_seconds',
      ),
  }),

  get_current_location_field: z.object({
    field: z
      .enum(['latitude', 'longitude', 'place_id', 'formatted_address'])
      .describe(
        'Name the current location info to retrieve. Can choose from\nlatitude, longitude, place_id, formatted_address',
      ),
  }),

  get_current_iso_8601_datetime_with_utc_offset: z.object({}),

  iso_8601_datetime_with_utc_offset_to_iso_weekday: z.object({
    datetime_str: z.string().describe('ISO 8601 datetime string with UTC offset.'),
  }),

  web_search: z.object({
    query: z.string().describe('Concise search query targeted for a search engine.'),
  }),

  weather_forecast: z.object({
    days: z
      .number()
      .int()
      .describe(
        "Number of days of forecast required. days parameter value ranges between 1 and 14.\ne.g: days=5. If no days parameter is provided then only today's weather is returned.\nShould not be provided with dt.",
      )
      .optional(),
    dt: z
      .string()
      .describe(
        'Forecast date in mm/dd/yyyy format. Should be between today and next 14 day.\nShould not be provided with days.',
      )
      .optional(),
    latlng: z
      .string()
      .describe('A comma separated string containing latitude and longitude. E.g. 48.8567,2.3508'),
  }),

  search_events: z.object({
    date: z
      .string()
      .describe(
        'Date for the event. Choose from\nany, today, tomorrow, week, weekend, next_week, month, next_month',
      )
      .optional(),
    is_virtual: z.boolean().describe('If true, only return virtual events.').optional(),
    query: z.string().describe('Search query including location. E.g. "Concert in San Francisco"'),
    start: z
      .number()
      .int()
      .describe(
        'Starting index within the search result list. The tool only returns 10 results at\na time. To fetch the remaining results, use the exact same rest of the arguments, and\nset start to your target offset.',
      )
      .optional(),
  }),

  search_spotify_top_results: z.object({ q: z.string().describe('Search query for spotify.') }),

  search_spotify_albums: z.object({
    limit: z
      .number()
      .int()
      .describe(
        'From the whole list, return a subsequence starting from offset, with length of limit.',
      )
      .optional(),
    offset: z
      .number()
      .int()
      .describe(
        'From the whole list, return a subsequence starting from offset, with length of limit.',
      )
      .optional(),
    q: z.string().describe('Search query for spotify.'),
  }),

  search_spotify_artists: z.object({
    limit: z
      .number()
      .int()
      .describe(
        'From the whole list, return a subsequence starting from offset, with length of limit.',
      )
      .optional(),
    offset: z
      .number()
      .int()
      .describe(
        'From the whole list, return a subsequence starting from offset, with length of limit.',
      )
      .optional(),
    q: z.string().describe('Search query for spotify.'),
  }),

  search_spotify_episodes: z.object({
    limit: z
      .number()
      .int()
      .describe(
        'From the whole list, return a subsequence starting from offset, with length of limit.',
      )
      .optional(),
    offset: z
      .number()
      .int()
      .describe(
        'From the whole list, return a subsequence starting from offset, with length of limit.',
      )
      .optional(),
    q: z.string().describe('Search query for spotify.'),
  }),

  search_spotify_genres: z.object({
    limit: z
      .number()
      .int()
      .describe(
        'From the whole list, return a subsequence starting from offset, with length of limit.',
      )
      .optional(),
    offset: z
      .number()
      .int()
      .describe(
        'From the whole list, return a subsequence starting from offset, with length of limit.',
      )
      .optional(),
    q: z.string().describe('Search query for spotify.'),
  }),

  search_spotify_playlists: z.object({
    limit: z
      .number()
      .int()
      .describe(
        'From the whole list, return a subsequence starting from offset, with length of limit.',
      )
      .optional(),
    offset: z
      .number()
      .int()
      .describe(
        'From the whole list, return a subsequence starting from offset, with length of limit.',
      )
      .optional(),
    q: z.string().describe('Search query for spotify.'),
  }),

  search_spotify_podcasts: z.object({
    limit: z
      .number()
      .int()
      .describe(
        'From the whole list, return a subsequence starting from offset, with length of limit.',
      )
      .optional(),
    offset: z
      .number()
      .int()
      .describe(
        'From the whole list, return a subsequence starting from offset, with length of limit.',
      )
      .optional(),
    q: z.string().describe('Search query for spotify.'),
  }),

  search_spotify_tracks: z.object({
    limit: z
      .number()
      .int()
      .describe(
        'From the whole list, return a subsequence starting from offset, with length of limit.',
      )
      .optional(),
    offset: z
      .number()
      .int()
      .describe(
        'From the whole list, return a subsequence starting from offset, with length of limit.',
      )
      .optional(),
    q: z.string().describe('Search query for spotify.'),
  }),

  spotify_album_details: z.object({
    ids: z
      .string()
      .describe(
        'A single album id, or multiple ids separated by comma.\nE.g. 2noRn2Aes5aoNVsU6iWThc,3XIgSzrNeDVXVc8LwP7axB',
      ),
  }),

  spotify_artist_profile: z.object({
    artist_id: z.string().describe('A single artist id.\nE.g. 4tZwfgrHOc3mvqYlEYSvVi'),
  }),

  spotify_artist_discography: z.object({
    artist_id: z.string().describe('A single artist id.\nE.g. 4tZwfgrHOc3mvqYlEYSvVi'),
  }),

  spotify_artist_stats: z.object({
    artist_id: z.string().describe('A single artist id.\nE.g. 4tZwfgrHOc3mvqYlEYSvVi'),
  }),

  spotify_artist_related_content: z.object({
    artist_id: z.string().describe('A single artist id.\nE.g. 4tZwfgrHOc3mvqYlEYSvVi'),
  }),

  spotify_artist_goods: z.object({
    artist_id: z.string().describe('A single artist id.\nE.g. 4tZwfgrHOc3mvqYlEYSvVi'),
  }),

  spotify_track_details: z.object({
    track_ids: z.string().describe('A single track id or comma separated ids.'),
  }),

  spotify_track_credits: z.object({ track_id: z.string().describe('A single track id.') }),

  spotify_playlist_tracks: z.object({
    limit: z
      .number()
      .int()
      .describe(
        'From the whole list, return a subsequence starting from offset, with length of limit.',
      )
      .optional(),
    offset: z
      .number()
      .int()
      .describe(
        'From the whole list, return a subsequence starting from offset, with length of limit.',
      )
      .optional(),
    playlist_id: z.string().describe('A single playlist id.'),
  }),

  spotify_episode_detail: z.object({ episode_id: z.string().describe('A single episode id.') }),

  scrape_webpage: z.object({ url: z.string().describe('URL to be scraped.') }),

  search_yelp: z.object({
    limit: z.number().int().describe('Number of results per page.').optional(),
    location: z
      .string()
      .describe(
        'String describing the region to search in.\nEnter exact locations. For example, use Roosevelt, NY not Roosevelt only.',
      ),
    offset: z
      .number()
      .int()
      .describe(
        'If offset is set to 0, it means start from zero.\nIf offset is set to 20, it means to start showing after 20 results.',
      )
      .optional(),
    search_term: z
      .string()
      .describe(
        'Enter any search term you want, just like on Yelp.\nEx. Coffee shop, Pizza shop, electrician, or plumber\nEx. Black Owned Saloon, Mexican pizza shop',
      ),
  }),

  business_details: z.object({
    business_ids: z
      .string()
      .describe(
        'Comma separated strings representing business ids.\nYou can put up to 39 business ids on each request.',
      ),
  }),

  business_reviews: z.object({
    business_id: z.string().describe('Business id').optional(),
    end_cursor: z
      .string()
      .describe(
        'Next page token. For next pages,\nif hasNextPage = true : Input the end_cursor value found from\nthe response of the previous page to get reviews of the next page.',
      )
      .optional(),
    rating_filter: z
      .string()
      .describe(
        'Only view reviews with rating of rating_filter. Choose from All_ratings,\n5_stars, 4_stars, 3_stars, 2_stars, 1_star',
      )
      .optional(),
    reviews_per_page: z.number().int().describe('Number of reviews per page').optional(),
    sort_by: z
      .string()
      .describe(
        'Sorting criteria. Choose from Yelp_sort, Newest_first, Oldest_first,\nHighest_rated, Lowest_rated, Elites.',
      )
      .optional(),
  }),

  business_menu: z.object({
    business_id: z.string().describe('String representing business ids.'),
  }),

  business_popular_dishes: z.object({
    business_id: z.string().describe('String representing business ids.'),
  }),

  product_search: z.object({
    category_id: z
      .string()
      .describe(
        'Find products in a specific category / department (e.g. computers-intl-ship).\nUse the Product Category List endpoint to get a list of valid categories and their ids for\nthe country specified in the request.\nFor numeric Amazon category IDs, use the category parameter instead.\nDefault: aps (All Departments)',
      )
      .optional(),
    country: z
      .string()
      .describe(
        'Sets the Amazon domain, marketplace country, language and currency.\nDefault: US\nAllowed values: US, AU, BR, CA, CN, FR, DE, IN, IT, MX, NL, SG, ES, TR, AE, GB,\nJP, SA, PL, SE, BE, EG',
      )
      .optional(),
    deals_and_discounts: z
      .string()
      .describe('Return deals and discounts in a specific condition.\nALL_DISCOUNTS, TODAYS_DEALS')
      .optional(),
    fields: z
      .string()
      .describe(
        'A comma separated list of product fields to include in the response (field projection).\nBy default all fields are returned.\nExample product_price,product_url,is_best_seller,sales_volume',
      )
      .optional(),
    four_stars_and_up: z
      .boolean()
      .describe('Return product listings with ratings of 4 stars & up.')
      .optional(),
    is_prime: z.boolean().describe('Only return prime products.').optional(),
    language: z
      .string()
      .describe(
        'The language of the results. In case not specified,\nresults will be returned in the default domain language.\nSupported languages per country\n    US en_US, es_US\n    AU en_AU\n    BR pt_BR\n    CA en_CA, fr_CA\n    FR fr_FR, en_GB\n    DE de_DE, en_GB, cs_CZ, nl_NL, pl_PL, tr_TR, da_DK\n    IN en_IN, hi_IN, ta_IN, te_IN, kn_IN, ml_IN, bn_IN, mr_IN\n    IT it_IT, en_GB\n    MX es_MX\n    NL nl_NL, en_GB\n    SG en_SG\n    ES es_ES, pt_PT, en_GB\n    TR tr_TR\n    AE en_AE, ar_AE\n    GB en_GB\n    JP ja_JP, en_US, zh_CN\n    SA ar_AE, en_AE\n    PL pl_PL\n    SE sv_SE, en_GB\n    BE fr_BE, nl_BE, en_GB\n    EG ar_AE, en_AE',
      )
      .optional(),
    max_price: z
      .number()
      .describe(
        'Only return product offers with price lower than a certain value.\n`               Specified in the currency of the selected country.\nFor example, in case country=US, a value of 105.34 means $105.34.',
      )
      .optional(),
    min_price: z
      .number()
      .describe(
        'Only return product offers with price greater than a certain value.\nSpecified in the currency of the selected country.\nFor example, in case country=US, a value of 105.34 means $105.34.',
      )
      .optional(),
    page: z.number().int().describe('Results page to return.').optional(),
    product_condition: z.string().describe('Return products in a specific condition.').optional(),
    query: z
      .string()
      .describe('Search query (supports both free-form text queries or a product asin).'),
    seller_id: z
      .string()
      .describe(
        'Find products sold by specific seller (merchant).\nMultiple sellers can be specified as a comma (,) separated list.',
      )
      .optional(),
    sort_by: z
      .string()
      .describe(
        'Return the results in a specific sort order.\nDefault: RELEVANCE\nAllowed values: RELEVANCE, LOWEST_PRICE, HIGHEST_PRICE, REVIEWS, NEWEST, BEST_SELLERS',
      )
      .optional(),
  }),

  product_details: z.object({
    asin: z
      .string()
      .describe(
        'Product ASIN for which to get details.\nSupports batching of up to 10 ASINs in a single request, separated by comma.',
      ),
    country: z
      .string()
      .describe(
        'Sets the Amazon domain, marketplace country, language and currency.\nDefault: US\nAllowed values: US, AU, BR, CA, CN, FR, DE, IN, IT, MX, NL, SG, ES, TR, AE, GB,\nJP, SA, PL, SE, BE, EG',
      )
      .optional(),
    fields: z
      .string()
      .describe(
        'A comma separated list of product fields to include in the response (field projection).\nBy default all fields are returned.\nExample product_price,product_url,is_best_seller,sales_volume',
      )
      .optional(),
    language: z
      .string()
      .describe(
        'The language of the results. In case not specified,\nresults will be returned in the default domain language.\nSupported languages per country\n    US en_US, es_US\n    AU en_AU\n    BR pt_BR\n    CA en_CA, fr_CA\n    FR fr_FR, en_GB\n    DE de_DE, en_GB, cs_CZ, nl_NL, pl_PL, tr_TR, da_DK\n    IN en_IN, hi_IN, ta_IN, te_IN, kn_IN, ml_IN, bn_IN, mr_IN\n    IT it_IT, en_GB\n    MX es_MX\n    NL nl_NL, en_GB\n    SG en_SG\n    ES es_ES, pt_PT, en_GB\n    TR tr_TR\n    AE en_AE, ar_AE\n    GB en_GB\n    JP ja_JP, en_US, zh_CN\n    SA ar_AE, en_AE\n    PL pl_PL\n    SE sv_SE, en_GB\n    BE fr_BE, nl_BE, en_GB\n    EG ar_AE, en_AE',
      )
      .optional(),
    more_info_query: z
      .string()
      .describe(
        'A query to search and get more info about the product as part of Product Information,\nCustomer Q&As, and Customer Reviews.',
      )
      .optional(),
    page: z.number().int().describe('Results page to return.').optional(),
  }),

  product_reviews: z.object({
    asin: z.string().describe('Product asin for which to get reviews.'),
    country: z
      .string()
      .describe(
        'Sets the Amazon domain, marketplace country, language and currency.\nDefault: US\nAllowed values: US, AU, BR, CA, CN, FR, DE, IN, IT, MX, NL, SG, ES, TR, AE, GB,\nJP, SA, PL, SE, BE, EG',
      )
      .optional(),
    current_format_only: z
      .boolean()
      .describe(
        'Only return reviews of the current format (product variant - e.g. Color).\nBy default reviews are returned for all product variants.',
      )
      .optional(),
    fields: z
      .string()
      .describe(
        'A comma separated list of product fields to include in the response (field projection).\nBy default all fields are returned.\nExample product_price,product_url,is_best_seller,sales_volume',
      )
      .optional(),
    images_or_videos_only: z
      .boolean()
      .describe('Only return reviews containing images and / or videos.')
      .optional(),
    language: z
      .string()
      .describe(
        'The language of the results. In case not specified,\nresults will be returned in the default domain language.\nSupported languages per country\n    US en_US, es_US\n    AU en_AU\n    BR pt_BR\n    CA en_CA, fr_CA\n    FR fr_FR, en_GB\n    DE de_DE, en_GB, cs_CZ, nl_NL, pl_PL, tr_TR, da_DK\n    IN en_IN, hi_IN, ta_IN, te_IN, kn_IN, ml_IN, bn_IN, mr_IN\n    IT it_IT, en_GB\n    MX es_MX\n    NL nl_NL, en_GB\n    SG en_SG\n    ES es_ES, pt_PT, en_GB\n    TR tr_TR\n    AE en_AE, ar_AE\n    GB en_GB\n    JP ja_JP, en_US, zh_CN\n    SA ar_AE, en_AE\n    PL pl_PL\n    SE sv_SE, en_GB\n    BE fr_BE, nl_BE, en_GB\n    EG ar_AE, en_AE',
      )
      .optional(),
    page: z.number().int().describe('Results page to return.').optional(),
    query: z.string().describe('Find reviews matching a search query.').optional(),
    sort_by: z
      .string()
      .describe(
        'Return reviews in a specific sort order.\nDefault: TOP_REVIEWS\nAllowed values: TOP_REVIEWS, MOST_RECENT',
      )
      .optional(),
    star_rating: z
      .string()
      .describe(
        'Only return reviews with a specific star rating.\nDefault: ALL\nAllowed values: ALL, 5_STARS, 4_STARS, 3_STARS, 2_STARS, 1_STARS, POSITIVE, CRITICAL',
      )
      .optional(),
    verified_purchases_only: z
      .boolean()
      .describe('Only return reviews by reviewers who made a verified purchase.')
      .optional(),
  }),

  seller_profile: z.object({
    country: z
      .string()
      .describe(
        'Sets the Amazon domain, marketplace country, language and currency.\nDefault: US\nAllowed values: US, AU, BR, CA, CN, FR, DE, IN, IT, MX, NL, SG, ES, TR, AE, GB,\nJP, SA, PL, SE, BE, EG',
      )
      .optional(),
    fields: z
      .string()
      .describe(
        'A comma separated list of product fields to include in the response (field projection).\nBy default all fields are returned.\nExample product_price,product_url,is_best_seller,sales_volume',
      )
      .optional(),
    language: z
      .string()
      .describe(
        'The language of the results. In case not specified,\nresults will be returned in the default domain language.\nSupported languages per country\n    US en_US, es_US\n    AU en_AU\n    BR pt_BR\n    CA en_CA, fr_CA\n    FR fr_FR, en_GB\n    DE de_DE, en_GB, cs_CZ, nl_NL, pl_PL, tr_TR, da_DK\n    IN en_IN, hi_IN, ta_IN, te_IN, kn_IN, ml_IN, bn_IN, mr_IN\n    IT it_IT, en_GB\n    MX es_MX\n    NL nl_NL, en_GB\n    SG en_SG\n    ES es_ES, pt_PT, en_GB\n    TR tr_TR\n    AE en_AE, ar_AE\n    GB en_GB\n    JP ja_JP, en_US, zh_CN\n    SA ar_AE, en_AE\n    PL pl_PL\n    SE sv_SE, en_GB\n    BE fr_BE, nl_BE, en_GB\n    EG ar_AE, en_AE',
      )
      .optional(),
    page: z.number().int().describe('Results page to return.').optional(),
    seller_id: z.string().describe('The Amazon Seller ID for which to get seller profile details'),
  }),

  seller_reviews: z.object({
    country: z
      .string()
      .describe(
        'Sets the Amazon domain, marketplace country, language and currency.\nDefault: US\nAllowed values: US, AU, BR, CA, CN, FR, DE, IN, IT, MX, NL, SG, ES, TR, AE, GB,\nJP, SA, PL, SE, BE, EG',
      )
      .optional(),
    fields: z
      .string()
      .describe(
        'A comma separated list of product fields to include in the response (field projection).\nBy default all fields are returned.\nExample product_price,product_url,is_best_seller,sales_volume',
      )
      .optional(),
    language: z
      .string()
      .describe(
        'The language of the results. In case not specified,\nresults will be returned in the default domain language.\nSupported languages per country\n    US en_US, es_US\n    AU en_AU\n    BR pt_BR\n    CA en_CA, fr_CA\n    FR fr_FR, en_GB\n    DE de_DE, en_GB, cs_CZ, nl_NL, pl_PL, tr_TR, da_DK\n    IN en_IN, hi_IN, ta_IN, te_IN, kn_IN, ml_IN, bn_IN, mr_IN\n    IT it_IT, en_GB\n    MX es_MX\n    NL nl_NL, en_GB\n    SG en_SG\n    ES es_ES, pt_PT, en_GB\n    TR tr_TR\n    AE en_AE, ar_AE\n    GB en_GB\n    JP ja_JP, en_US, zh_CN\n    SA ar_AE, en_AE\n    PL pl_PL\n    SE sv_SE, en_GB\n    BE fr_BE, nl_BE, en_GB\n    EG ar_AE, en_AE',
      )
      .optional(),
    page: z.number().int().describe('Results page to return.').optional(),
    seller_id: z.string().describe('The Amazon Seller ID for which to get seller profile details'),
    star_rating: z
      .string()
      .describe(
        'Only return reviews with a specific star rating or positive / negative sentiment.\nDefault: ALL\nAllowed values: ALL, 5_STARS, 4_STARS, 3_STARS, 2_STARS, 1_STARS, POSITIVE, CRITICAL',
      )
      .optional(),
  }),

  seller_products: z.object({
    country: z
      .string()
      .describe(
        'Sets the Amazon domain, marketplace country, language and currency.\nDefault: US\nAllowed values: US, AU, BR, CA, CN, FR, DE, IN, IT, MX, NL, SG, ES, TR, AE, GB,\nJP, SA, PL, SE, BE, EG',
      )
      .optional(),
    fields: z
      .string()
      .describe(
        'A comma separated list of product fields to include in the response (field projection).\nBy default all fields are returned.\nExample product_price,product_url,is_best_seller,sales_volume',
      )
      .optional(),
    language: z
      .string()
      .describe(
        'The language of the results. In case not specified,\nresults will be returned in the default domain language.\nSupported languages per country\n    US en_US, es_US\n    AU en_AU\n    BR pt_BR\n    CA en_CA, fr_CA\n    FR fr_FR, en_GB\n    DE de_DE, en_GB, cs_CZ, nl_NL, pl_PL, tr_TR, da_DK\n    IN en_IN, hi_IN, ta_IN, te_IN, kn_IN, ml_IN, bn_IN, mr_IN\n    IT it_IT, en_GB\n    MX es_MX\n    NL nl_NL, en_GB\n    SG en_SG\n    ES es_ES, pt_PT, en_GB\n    TR tr_TR\n    AE en_AE, ar_AE\n    GB en_GB\n    JP ja_JP, en_US, zh_CN\n    SA ar_AE, en_AE\n    PL pl_PL\n    SE sv_SE, en_GB\n    BE fr_BE, nl_BE, en_GB\n    EG ar_AE, en_AE',
      )
      .optional(),
    page: z.number().int().describe('Results page to return.').optional(),
    seller_id: z.string().describe('The Amazon Seller ID for which to get seller profile details'),
    sort_by: z
      .string()
      .describe(
        'Return the results in a specific sort order.\nDefault: RELEVANCE\nAllowed values: RELEVANCE, LOWEST_PRICE, HIGHEST_PRICE, REVIEWS, NEWEST, BEST_SELLERS',
      )
      .optional(),
  }),

  get_product_categories: z.object({
    country: z
      .string()
      .describe(
        'Sets the Amazon domain, marketplace country, language and currency.\nDefault: US\nAllowed values: US, AU, BR, CA, CN, FR, DE, IN, IT, MX, NL, SG, ES, TR, AE, GB,\nJP, SA, PL, SE, BE, EG',
      )
      .optional(),
  }),

  get_latest_news: z.object({
    lr: z
      .string()
      .describe(
        'Language region parameter to specify the desired language and region of the news.\nDefault: en-US',
      )
      .optional(),
  }),

  get_world_news: z.object({
    lr: z
      .string()
      .describe(
        'Language region parameter to specify the desired language and region of the news.\nIf not provided, the default value will be used.',
      )
      .optional(),
  }),

  get_business_news: z.object({
    lr: z
      .string()
      .describe(
        'Language region parameter to specify the desired language and region of the news.\nDefault: en-US',
      )
      .optional(),
  }),

  get_entertainment_news: z.object({
    lr: z
      .string()
      .describe(
        'Language region parameter to specify the desired language and region of the news.\nDefault: en-US',
      )
      .optional(),
  }),

  get_health_news: z.object({
    lr: z
      .string()
      .describe(
        'Language region parameter to specify the desired language and region of the news.\nDefault: en-US',
      )
      .optional(),
  }),

  get_science_news: z.object({
    lr: z
      .string()
      .describe(
        'Language region parameter to specify the desired language and region of the news.\nDefault: en-US',
      )
      .optional(),
  }),

  get_sport_news: z.object({
    lr: z
      .string()
      .describe(
        'Language region parameter to specify the desired language and region of the news.\nDefault: en-US',
      )
      .optional(),
  }),

  get_technology_news: z.object({
    lr: z
      .string()
      .describe(
        'Language region parameter to specify the desired language and region of the news.\nDefault: en-US',
      )
      .optional(),
  }),

  search_news: z.object({
    keyword: z
      .string()
      .describe(
        'The search keyword to find relevant news articles.\nCan be any topic, person, company, event, or subject of interest.\nExamples: "COVID-19", "Apple", "climate change", "Olympics", "AI technology"',
      ),
    lr: z
      .string()
      .describe(
        'Language region parameter to specify the desired language and region of the news.\nIf not provided, the default value will be used.',
      )
      .optional(),
  }),

  get_search_suggestions: z.object({
    keyword: z
      .string()
      .describe(
        'The user input for keyword search suggestions.\nCan be partial words or phrases to get autocomplete suggestions.\nExamples: "econ" (might suggest "economy", "economics"),\n"tech" (might suggest "technology", "tech news"), "clim" (might suggest "climate")',
      ),
    lr: z
      .string()
      .describe(
        'Language region parameter to specify the desired language and region of the\nsearch suggestions.\nIf not provided, the default value will be used.',
      )
      .optional(),
  }),

  get_country_holidays_by_year: z.object({
    country_code: z
      .string()
      .describe(
        'The ISO 3166-1 alpha-2 country code (e.g., "US" for United States,\n"GB" for United Kingdom, "CA" for Canada, "DE" for Germany).',
      ),
    year: z
      .number()
      .int()
      .describe(
        'The year for which to retrieve holidays (e.g., 2023, 2024).\nMust be a valid 4-digit year. Most countries have data available\nfor recent years and some future years.',
      ),
  }),

  get_supported_country_codes: z.object({}),

  search_reddit: z.object({
    filter: z
      .string()
      .describe(
        'The type of content to filter and return.\nDefault: "posts"\nAllowed values: "posts", "comments", "media", "users", "communities"\nNote: "users" and "communities" filters are only available for sitewide searches',
      )
      .optional(),
    search: z
      .string()
      .describe(
        'The search term to look for. Can be any keyword or phrase.\nExample: "investing", "best hiking trail in the bay area"',
      ),
    sort_type: z
      .string()
      .describe(
        'How to sort the search results.\nDefault: "relevance"\nAllowed values: "relevance", "hot", "top", "new", "comments"',
      )
      .optional(),
    subreddit: z
      .string()
      .describe(
        'A specific subreddit to search within. Leave None to search sitewide.\nExample: "wallstreetbets",\nNote: Do not include the "r/" prefix',
      )
      .optional(),
    time_filter: z
      .string()
      .describe(
        'The time period to filter results by.\nDefault: "all"\nAllowed values: "all", "year", "month", "week", "day", "hour"\nNote: Only applicable when sort_type is "relevance" or "comments"',
      )
      .optional(),
  }),

  get_subreddit_posts: z.object({
    filter: z
      .string()
      .describe(
        'The type of posts to retrieve based on Reddit\'s sorting options.\nDefault: "hot"\nAllowed values: "hot", "new", "top"\n- "hot": Currently trending posts with high engagement\n- "new": Most recently posted content\n- "top": Highest scoring posts',
      )
      .optional(),
    url: z
      .string()
      .describe(
        'The full URL of the subreddit to get posts from.\nMust be a valid Reddit subreddit URL.\nExample: "https://www.reddit.com/r/wallstreetbets"\nExample: "https://www.reddit.com/r/programming"\nExample: "https://www.reddit.com/r/MachineLearning"',
      ),
  }),

  get_post_details: z.object({
    url: z
      .string()
      .describe(
        'The full URL of the Reddit post to get details for.\nMust be a valid Reddit post URL that includes the post ID.\nExample: "https://www.reddit.com/r/wallstreetbets/comments/p0esdp/do_hedge_funds_beat_the_market_i_analyzed_the"',
      ),
  }),

  get_market_tickers: z.object({
    asset_type: z
      .string()
      .describe(
        'Enter one of the following assetClass: STOCKS or ETF or MUTUALFUNDS.\nExample: STOCKS',
      )
      .optional(),
    page: z.number().int().describe('Enter a page number.').optional(),
  }),

  search_securities: z.object({
    search: z
      .string()
      .describe(
        'Search query string. Can be a company name, ticker symbol, or partial match.\nExamples: "Apple", "AAPL", "Tesla", "TSLA"',
      ),
  }),

  get_market_quotes_snapshots: z.object({
    tickers: z
      .string()
      .describe(
        'Comma-separated list of ticker symbols. Maximum 200 symbols.\nSupported formats:\n- Equity: A, AAPL, GM, MU, TSLA\n- Futures: ALI=F, CD=F, QM=F, ^IXIC, BTC-F\n- Forex: EURUSD=X, AUDUSD=X\n- Indexes: ^DJT, ^HSI, ^VIX, ^TRFK-TC\n- Mutual Funds & ETFs: SPY, AWSHX, VOO, XAIX.BE\n- Cryptocurrencies: BTC-USD, ETH-USD\n- Combination: AAPL,CD=F,^VIX,ADA-USD',
      ),
  }),

  get_market_quotes: z.object({
    asset_type: z
      .string()
      .describe(
        'Type of asset to get quote for.\nDefault: STOCKS\nSupported types: STOCKS, ETF, MUTUALFUNDS',
      )
      .optional(),
    ticker: z
      .string()
      .describe(
        'Single ticker symbol to get quote for.\nExamples: AAPL, MSFT, TSLA, ^SPX, BTC-USD',
      ),
  }),

  get_stock_history: z.object({
    dividend: z
      .boolean()
      .describe(
        'Include dividend information in the response.\nOptional, set to True to include dividends',
      )
      .optional(),
    interval: z
      .string()
      .describe(
        'Data interval/granularity.\nDefault: 1d\nValid intervals: 1m, 2m, 3m, 4m, 5m, 15m, 30m, 1h, 1d, 1wk, 1mo, 1qty',
      )
      .optional(),
    limit: z
      .number()
      .int()
      .describe('Limit the number of candles returned.\nOptional, range: 1-1000')
      .optional(),
    symbol: z
      .string()
      .describe(
        'Stock symbol to get historical data for.\nExamples: AAPL, MSFT, TSLA, ^SPX, BTC-USD',
      ),
  }),

  screen_stocks: z.object({
    list_type: z
      .string()
      .describe(
        "Type of stock screener to use.\nDefault: most_actives\nAvailable screeners:\n- trending: Trending tickers in today's market\n- undervalued_growth_stocks: Stocks with earnings growth\n- growth_technology_stocks: Technology stocks with revenue\n- day_gainers: Stocks with the highest gains\n- day_losers: Stocks with the highest losses\n- most_actives: Stocks by intraday trade volume\n- undervalued_large_caps: Undervalued large cap stocks\n- aggressive_small_caps: Small-cap stocks with earnings growth\n- small_cap_gainers: Small caps with a 1 day price change of 5.0%",
      )
      .optional(),
  }),

  get_stock_modules: z.object({
    module: z
      .string()
      .describe(
        'Single module to retrieve (one per request).\nDefault: profile\nAvailable modules:\n- profile: Company profile and business description\n- income-statement: Annual income statements\n- balance-sheet: Annual balance sheets\n- cashflow-statement: Annual cash flow statements\n- statistics: Key financial statistics\n- calendar-events: Upcoming calendar events\n- sec-filings: SEC filings\n- recommendation-trend: Analyst recommendation trends\n- upgrade-downgrade-history: Upgrade/downgrade history\n- institution-ownership: Institutional ownership data\n- fund-ownership: Fund ownership data\n- major-directHolders: Major direct holders\n- major-holders-breakdown: Major holders breakdown\n- insider-transactions: Insider transaction data\n- insider-holders: Insider holders data\n- net-share-purchase-activity: Net share purchase activity\n- earnings: Earnings data\n- industry-trend: Industry trend data\n- index-trend: Index trend data\n- sector-trend: Sector trend data',
      )
      .optional(),
    ticker: z
      .string()
      .describe('Stock symbol to get module data for.\nExamples: AAPL, MSFT, TSLA, ^SPX'),
  }),

  get_insider_trades: z.object({}),

  get_market_news: z.object({
    news_type: z
      .string()
      .describe(
        'Type of news to retrieve (optional).\nAvailable types:\n- ALL: All types of news\n- VIDEO: Video news only\n- PRESS_RELEASE: Press releases only',
      )
      .optional(),
    tickers: z
      .string()
      .describe('Stock symbol to get news for (optional).\nExamples: AAPL, MSFT, TSLA')
      .optional(),
  }),
};
